import * as React from "react";
import { useCallback, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button, Combobox, Dialog, Input } from "@/components/ui";
import { apiErrorMessage, Fan, FanServices, FormResponse, FormServices } from "@/services/bedrock";
import { usePaginatedCombobox } from "@/utils";
import { ChoiceCards } from "./FormRoutes/components/RouteCard";

interface Props {
  /** Mounted fresh for each response (keyed by it), so nothing picked for one response carries over. */
  response: FormResponse;
  onOpenChange: (open: boolean) => void;
}

interface FanOption {
  id: string | number;
  label: string;
  original: Fan;
}

export function MoveResponseDialog({ response, onOpenChange }: Props) {
  const queryClient = useQueryClient();
  const submitted = response.submittedIdentity ?? null;
  const [mode, setMode] = useState<"existing" | "new">("existing");
  const [details, setDetails] = useState({
    displayName: submitted?.displayName ?? "",
    email: submitted?.email ?? "",
    phoneNumber: submitted?.phone ?? "",
  });

  const fetchFans = useCallback(
    async (search: string | undefined, page: number) => {
      const result = await FanServices.getFans({ search, page, perPage: 25, active: true });
      return {
        ...result,
        content: result.content
          .filter(fan => String(fan.id) !== String(response.fan?.id))
          .map(fan => ({
            id: fan.id,
            label: `${fan.displayName || "No Name"}${fan.email ? ` - ${fan.email}` : ""}`,
            original: fan,
          })),
      };
    },
    [response.fan?.id]
  );
  const fans = usePaginatedCombobox(fetchFans, false);
  const selectedFan = (fans.value as FanOption | null)?.original ?? null;

  const targetName =
    mode === "existing"
      ? selectedFan
        ? selectedFan.displayName || selectedFan.email || "this Fan"
        : null
      : details.displayName.trim() || details.email.trim() || "a new Fan";

  const move = useMutation({
    mutationFn: () =>
      FormServices.moveFormResponse(
        response.id,
        mode === "existing"
          ? { fanId: selectedFan!.id }
          : {
              createFan: {
                displayName: details.displayName.trim() || undefined,
                email: details.email.trim() || undefined,
                phoneNumber: details.phoneNumber.trim() || undefined,
              },
            }
      ),
    onSuccess: () => {
      toast.success(`Response moved to ${targetName}`);
      queryClient.invalidateQueries({ queryKey: ["formResponsesList"] });
      onOpenChange(false);
    },
    onError: e => toast.error(apiErrorMessage(e, "The response couldn't be moved. Try again.")),
  });

  const canSubmit = mode === "existing" ? !!selectedFan : !!(details.displayName.trim() || details.email.trim());

  return (
    <Dialog open onOpenChange={onOpenChange} size="md">
      <Dialog.Header>
        <Dialog.Title>Move to another Fan</Dialog.Title>
        <Dialog.Description>Choose who this response really belongs to.</Dialog.Description>
      </Dialog.Header>
      <Dialog.Content className="flex flex-col gap-4 py-4">
        <ChoiceCards
          label="Move to"
          value={mode}
          onChange={setMode}
          options={[
            { value: "existing", label: "A Fan you already have" },
            {
              value: "new",
              label: "Create a new Fan from the submitted details",
              description: submitted ? undefined : "Nothing was typed on the form, so fill in the details yourself.",
            },
          ]}
        />
        {mode === "existing" ? (
          <Combobox {...fans} placeholder="Search Fans by name or email..." className="w-full" />
        ) : (
          <div className="grid gap-3">
            <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
              Name
              <Input
                value={details.displayName}
                onChange={e => setDetails(d => ({ ...d, displayName: e.target.value }))}
              />
            </label>
            <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
              Email
              <Input value={details.email} onChange={e => setDetails(d => ({ ...d, email: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
              Phone
              <Input
                value={details.phoneNumber}
                onChange={e => setDetails(d => ({ ...d, phoneNumber: e.target.value }))}
              />
            </label>
          </div>
        )}
        {targetName && (
          <p className="text-sm text-slate-600" data-testid="move-confirm">
            Move this response to {targetName}? Its review moves too. The original Fan keeps their form link.
          </p>
        )}
      </Dialog.Content>
      <Dialog.Footer>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={move.isPending}>
            Cancel
          </Button>
          <Button onClick={() => move.mutate()} loading={move.isPending} disabled={!canSubmit}>
            Move response
          </Button>
        </div>
      </Dialog.Footer>
    </Dialog>
  );
}
